// #region Imports
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  RouteReuseStrategy,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

// Entry point di Ionic per singola funzione: il barrel '@ionic/angular' porterebbe nel bundle iniziale tutti i
// componenti di Ionic.
import { IonicRouteStrategy } from '@ionic/angular/ionic-route-strategy';
import { provideIonicAngular } from '@ionic/angular/provide';

import { baseRoutes } from './app.routes';
import { NetworkAwarePreloadingStrategy } from './core/routing/network-aware-preloading.strategy';
import { LanguageService } from './services/language.service';
import { registerAppIcons } from './shared/base-components/app-icons';
// #endregion Imports

// I custom element di Swiper (swiper-container, swiper-slide) li registra ciascun file che li usa nel template
// (le due homepage e i prodotti suggeriti del carrello), al proprio caricamento e prima del proprio disegno.

export const appConfig: ApplicationConfig = {
  providers: [
    // Rilevamento dei cambiamenti senza Zone.js: le viste si aggiornano quando cambia un signal letto dal template,
    // per un evento del template o un input; tutto lo stato mostrato dall'app è in signal.
    provideZonelessChangeDetection(),
    // Senza Zone.js gli errori non gestiti nei timer, nelle promesse e negli eventi del browser non passano più da
    // soli per l'ErrorHandler: questi ascoltatori di window ('error' e 'unhandledrejection') ce li riportano,
    // così finiscono nella stessa segnalazione di prima.
    provideBrowserGlobalErrorListeners(),
    // All'avvio il router conosce solo le rotte di base; il resto lo aggiunge BootstrapService. URL senza hash
    // (strategia predefinita) e navigazione verso lo stesso URL che ricarica la rotta. Le pagine lazy si
    // precaricano una alla volta, solo su connessione veloce e quando la pagina aperta ha finito le sue richieste,
    // per non togliere rete al primo disegno. Lo scroller del router c'era sempre con la configurazione di radice:
    // resta, con il ripristino della posizione e lo scorrimento alle ancore disattivati come prima.
    // Nessun provider di @angular/animations: nessun componente dell'app né delle librerie usa trigger di
    // animazione Angular (ngx-spinner usa animate.leave del core).
    provideRouter(
      baseRoutes,
      withPreloading(NetworkAwarePreloadingStrategy),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling(),
    ),
    // Ionic in build standalone: stesse opzioni di prima (solo modalità ios, HTML nei messaggi degli
    // overlay). useSetInputAPI fa arrivare i componentProps dei modali con setInput(), così i componenti
    // dei modali possono dichiararli come input() e ricevono i valori prima di ngOnInit come oggi.
    // Registra anche ModalController, PopoverController e AngularDelegate nell'injector di root.
    provideIonicAngular({ mode: 'ios', innerHTMLTemplatesEnabled: true, useSetInputAPI: true }),
    // Icone di ionicons registrate prima del primo disegno: con la build standalone un nome non registrato
    // né raggiungibile dal percorso degli asset resterebbe vuoto.
    provideAppInitializer(() => registerAppIcons()),
    // Risolve il prefisso lingua in URL (es. /EN/pagina) prima della prima navigazione del Router,
    // così AppComponent legge un window.location già ripulito e non serve navigare due volte.
    provideAppInitializer(() => inject(LanguageService).initLanguageFromUrl()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
};
